import { createContext, PropsWithChildren, useContext, useState } from 'react';
import { DeclarationCountry } from 'store/declarations/enums/common/declaration-country';
import { DeclarationInternalType } from 'store/declarations/enums/common/declaration-internal-type';
import { JobResponse } from 'store/jobs/job';
import { Manifest } from 'store/manifests/types';
import { BrokerUploadLocationState } from './types';

type BrokerUploadContextType = {
    country: DeclarationCountry | undefined;
    type: DeclarationInternalType | undefined;
    locationState: BrokerUploadLocationState | null;
    selectedJob: JobResponse | undefined;
    setSelectedJob: (job: JobResponse | undefined) => void;
    selectedManifest: Manifest | undefined;
    setSelectedManifest: (manifest: Manifest | undefined) => void;
    manifestId: string | undefined;
};

const BrokerUploadContext = createContext<BrokerUploadContextType | null>(null);

export const useBrokerUploadContext = () => {
    const ctx = useContext(BrokerUploadContext);
    if (!ctx) throw new Error('useBrokerUploadContext must be used within BrokerUploadProvider');
    return ctx;
};

type Props = PropsWithChildren<{
    country: DeclarationCountry | undefined;
    type: DeclarationInternalType | undefined;
    locationState: BrokerUploadLocationState | null;
    initialJob: JobResponse | undefined;
    initialManifest: Manifest | undefined;
}>;

export const BrokerUploadProvider = ({
    children,
    country,
    type,
    locationState,
    initialJob,
    initialManifest,
}: Props) => {
    const [selectedJob, setSelectedJob] = useState<JobResponse | undefined>(initialJob);
    const [selectedManifest, setSelectedManifest] = useState<Manifest | undefined>(initialManifest);
    const manifestId = selectedManifest?.id ?? locationState?.manifestId;

    return (
        <BrokerUploadContext.Provider
            value={{
                country,
                type,
                locationState,
                selectedJob,
                setSelectedJob,
                selectedManifest,
                setSelectedManifest,
                manifestId,
            }}
        >
            {children}
        </BrokerUploadContext.Provider>
    );
};
